import { Injectable, signal } from '@angular/core';
import {
  ApplicationScheme,
  applicationSchemeToSlug,
  normalizeApplicationScheme,
} from '../../shared/constants/application-scheme';
import {
  WIZARD_BRANDING_METADATA,
  type WizardBrandingMetadata,
  type WizardBrandKey,
} from './wizard-branding.metadata';

@Injectable({ providedIn: 'root' })
export class WizardBrandingService {
  private static readonly CEB_HEADER_BG_VAR = 'var(--ceb-header-bg, oklch(28% 0.09 255deg))';
  private static readonly CEB_FOOTER_BG_VAR = 'var(--ceb-footer-bg, oklch(20% 0.09 255deg))';
  private static readonly CEB_PRIMARY_COLOR = '#00206f';
  private static readonly CEB_PRIMARY_DARK = 'var(--ceb-header-bg, oklch(28% 0.09 255deg))';
  private static readonly CEB_ACCENT_COLOR = 'oklch(0.795 0.175 83)';
  private static readonly CEB_PAGE_BG = '#f4f7fb';
  private static readonly CEB_SHELL_BG = '#eef3f8';
  private static readonly CEB_TEXT = '#10213e';
  private static readonly CEB_TEXT_MUTED = '#5f6d84';
  private static readonly CEB_BORDER = '#cad4e1';
  private static readonly CEB_BORDER_SOFT = '#e4ebf3';
  private static readonly CEB_TABLE_HEADER_BG = '#edf2f8';
  private static readonly CEB_TABLE_ROW_BG = '#ffffff';
  private static readonly CEB_TABLE_ROW_HOVER_BG = '#f7f9fc';
  private static readonly CEB_PREV_BUTTON_BG = '#e8eef6';

  private readonly activeBrandingSignal = signal<WizardBrandingMetadata>(this.getInitialBranding());

  readonly activeBranding = this.activeBrandingSignal.asReadonly();

  constructor() {
    this.applyBrandingCssVariables(this.activeBrandingSignal());
  }

  private getInitialBranding(): WizardBrandingMetadata {
    if (typeof window === 'undefined') {
      return WIZARD_BRANDING_METADATA.default;
    }

    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return this.resolveBrandingFromUrl(path);
  }

  setActiveBrandingFromUrl(url: string): WizardBrandingMetadata {
    const metadata = this.resolveBrandingFromUrl(url);
    this.activeBrandingSignal.set(metadata);
    this.applyBrandingCssVariables(metadata);
    return metadata;
  }

  setActiveBrandingFromApplicationScheme(applicationScheme: string | null | undefined): WizardBrandingMetadata {
    const normalizedScheme = normalizeApplicationScheme(applicationScheme);
    const scheme = normalizedScheme === ApplicationScheme.OTHER
      ? ApplicationScheme.CNCS
      : normalizedScheme;
    const metadata = this.getBrandingByKey(applicationSchemeToSlug(scheme));
    this.activeBrandingSignal.set(metadata);
    this.applyBrandingCssVariables(metadata);
    return metadata;
  }

  resolveBrandingFromUrl(url: string): WizardBrandingMetadata {
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    const firstSegment = segments[0];

    if (!firstSegment) {
      return WIZARD_BRANDING_METADATA.default;
    }

    if (firstSegment === 'application' || firstSegment === 'cncs-wizard') {
      return WIZARD_BRANDING_METADATA.cncs;
    }

    if (segments[1] === 'application') {
      return this.getBrandingByKey(firstSegment);
    }

    return WIZARD_BRANDING_METADATA.default;
  }

  private getBrandingByKey(key: string): WizardBrandingMetadata {
    if (this.isWizardBrandKey(key)) {
      return WIZARD_BRANDING_METADATA[key];
    }

    return WIZARD_BRANDING_METADATA.default;
  }

  private isWizardBrandKey(key: string): key is Exclude<WizardBrandKey, 'default'> {
    return key in WIZARD_BRANDING_METADATA && key !== 'default';
  }

  private applyBrandingCssVariables(metadata: WizardBrandingMetadata): void {
    if (typeof document === 'undefined') {
      return;
    }

    const rootStyle = document.documentElement.style;
    const headerBg = metadata.primaryColor || WizardBrandingService.CEB_PRIMARY_COLOR;
    const headerBgDark = metadata.primaryColorDark
      || this.adjustHexColor(headerBg, -0.18)
      || WizardBrandingService.CEB_PRIMARY_DARK;
    const primaryColor = headerBg;
    const primaryDark = headerBgDark;
    const ionicPrimaryHex = primaryColor;
    const primaryRgb = this.hexToRgb(ionicPrimaryHex);
    const primaryShade = this.adjustHexColor(ionicPrimaryHex, -0.12);
    const primaryTint = this.adjustHexColor(ionicPrimaryHex, 0.08);
    const primaryShadeRgb = this.hexToRgb(primaryShade);
    const primaryTintRgb = this.hexToRgb(primaryTint);
    const pageBg = this.mixHexColors(primaryColor, WizardBrandingService.CEB_PAGE_BG, 0.94);
    const shellBg = this.mixHexColors(primaryColor, WizardBrandingService.CEB_SHELL_BG, 0.9);
    const canvasBg = WizardBrandingService.CEB_TABLE_ROW_BG;
    const textColor = this.mixHexColors(primaryDark, WizardBrandingService.CEB_TEXT, 0.72);
    const mutedTextColor = this.mixHexColors(primaryDark, WizardBrandingService.CEB_TEXT_MUTED, 0.76);
    const borderColor = this.mixHexColors(primaryColor, WizardBrandingService.CEB_BORDER, 0.82);
    const borderSoftColor = this.mixHexColors(primaryColor, WizardBrandingService.CEB_BORDER_SOFT, 0.88);
    const tableHeaderBg = this.mixHexColors(primaryColor, WizardBrandingService.CEB_TABLE_HEADER_BG, 0.88);
    const tableRowHoverBg = this.mixHexColors(primaryColor, WizardBrandingService.CEB_TABLE_ROW_HOVER_BG, 0.94);
    const previousButtonBg = this.mixHexColors(primaryColor, WizardBrandingService.CEB_PREV_BUTTON_BG, 0.84);

    rootStyle.setProperty('--ceb-header-bg', primaryColor);
    rootStyle.setProperty('--ceb-footer-bg', primaryDark);
    rootStyle.setProperty('--ceb-accent', WizardBrandingService.CEB_ACCENT_COLOR);
    rootStyle.setProperty('--main-bg-color', WizardBrandingService.CEB_FOOTER_BG_VAR);
    rootStyle.setProperty('--app-primary', WizardBrandingService.CEB_HEADER_BG_VAR);
    rootStyle.setProperty('--wizard-primary', WizardBrandingService.CEB_HEADER_BG_VAR);
    rootStyle.setProperty('--wizard-primary-dark', WizardBrandingService.CEB_FOOTER_BG_VAR);
    // rootStyle.setProperty('--wizard-primary-rgb', primaryRgb);
    // rootStyle.setProperty('--wizard-page-bg', pageBg);
    // rootStyle.setProperty('--wizard-shell-bg', pageBg);
    // rootStyle.setProperty('--wizard-sidebar-bg', shellBg);
    // rootStyle.setProperty('--wizard-canvas-bg', canvasBg);
    // rootStyle.setProperty('--wizard-text', textColor);
    // rootStyle.setProperty('--wizard-text-muted', mutedTextColor);
    // rootStyle.setProperty('--wizard-border', borderColor);
    // rootStyle.setProperty('--wizard-border-soft', borderSoftColor);
    // rootStyle.setProperty('--wizard-table-header-bg', tableHeaderBg);
    // rootStyle.setProperty('--wizard-table-row-bg', canvasBg);
    // rootStyle.setProperty('--wizard-table-row-bg-hover', tableRowHoverBg);
    // rootStyle.setProperty('--wizard-btn-prev-bg', previousButtonBg);
    rootStyle.setProperty('--ion-color-primary', WizardBrandingService.CEB_HEADER_BG_VAR);
    // rootStyle.setProperty('--ion-color-primary-rgb', primaryRgb);
    // rootStyle.setProperty('--ion-color-primary-contrast', '#ffffff');
    // rootStyle.setProperty('--ion-color-primary-contrast-rgb', '255, 255, 255');
    // rootStyle.setProperty('--ion-color-primary-shade', primaryShade);
    // rootStyle.setProperty('--ion-color-primary-shade-rgb', primaryShadeRgb);
    // rootStyle.setProperty('--ion-color-primary-tint', primaryTint);
    // rootStyle.setProperty('--ion-color-primary-tint-rgb', primaryTintRgb);
  }

  private hexToRgb(hex: string): string {
    const normalized = hex.replace('#', '');
    const safeHex = normalized.length === 3
      ? normalized.split('').map((value) => `${value}${value}`).join('')
      : normalized.padStart(6, '0').slice(0, 6);

    const red = Number.parseInt(safeHex.slice(0, 2), 16);
    const green = Number.parseInt(safeHex.slice(2, 4), 16);
    const blue = Number.parseInt(safeHex.slice(4, 6), 16);

    return `${red}, ${green}, ${blue}`;
  }

  private adjustHexColor(hex: string, amount: number): string {
    const normalized = hex.replace('#', '');
    const safeHex = normalized.length === 3
      ? normalized.split('').map((value) => `${value}${value}`).join('')
      : normalized.padStart(6, '0').slice(0, 6);

    const channels = [0, 2, 4].map((index) => Number.parseInt(safeHex.slice(index, index + 2), 16));
    const adjusted = channels.map((channel) => {
      const next = amount >= 0
        ? channel + (255 - channel) * amount
        : channel * (1 + amount);
      return Math.max(0, Math.min(255, Math.round(next)));
    });

    return `#${adjusted.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
  }

  private mixHexColors(baseHex: string, targetHex: string, weight: number): string {
    const normalizedWeight = Math.max(0, Math.min(1, weight));
    const baseChannels = this.hexToChannels(baseHex);
    const targetChannels = this.hexToChannels(targetHex);
    const mixedChannels = baseChannels.map((channel, index) => Math.round(
      channel * (1 - normalizedWeight) + targetChannels[index] * normalizedWeight,
    ));

    return `#${mixedChannels.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
  }

  private hexToChannels(hex: string): number[] {
    const normalized = hex.replace('#', '');
    const safeHex = normalized.length === 3
      ? normalized.split('').map((value) => `${value}${value}`).join('')
      : normalized.padStart(6, '0').slice(0, 6);

    return [0, 2, 4].map((index) => Number.parseInt(safeHex.slice(index, index + 2), 16));
  }
}
